import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Box, IconButton } from "@mui/material";
import { DateTime } from "luxon";
import type React from "react";
import type { CSSProperties } from "react";
import { type FC, useCallback, useMemo, useState } from "react";
import { EditTimePopover } from "@/components/EditTimePopover";
import { useDepartmentFlags } from "@/hooks/useDepartmentFlags";
import { INK, NEUTRAL } from "@/theme/tokens";
import { type DropdownItem, SystemDropdown } from "@/ui/Inputs/SystemDropDown";
import { SystemValidationTextField } from "@/ui/Inputs/SystemValidationTextField";
import { enumToDropdownItems } from "@/utils/utilFunctions";

export enum MessageWorkingFilterEnum {
	STAFF_THAT_WORKS = "Staff that works",
	STAFF_THAT_DOES_NOT_WORK = "Staff that does not work",
}

type MessageWorkingFilterKeys = keyof typeof MessageWorkingFilterEnum;
const messageWorkingFilterItems: DropdownItem<MessageWorkingFilterKeys>[] = enumToDropdownItems(MessageWorkingFilterEnum);

export type MessageFiltersType = {
	workFilter: MessageWorkingFilterEnum;
	dateFilter: DateTime;
	timeFilter: { start: DateTime; end: DateTime };
};

type Props = {
	/**
	 * Unique identifier for the filter, used to manage multiple filters.
	 */
	filterUuid: string;
	onChange?: (filters: MessageFiltersType, filterUuid: string) => void;
	onDelete?: (filterUuid: string) => void;
	isMobile?: boolean;
} & MessageFiltersType;

const commonStyle = {
	background: NEUTRAL[50],
} as const satisfies CSSProperties;

export const MessageFilter: FC<Props> = ({ workFilter, dateFilter, timeFilter, onChange, onDelete, filterUuid, isMobile }) => {
	const { timezone } = useDepartmentFlags();
	const [timePickerAnchor, setTimePickerAnchor] = useState<HTMLElement | null>(null);
	const workFilterMemo = useMemo<MessageWorkingFilterKeys | undefined>(
		() => messageWorkingFilterItems.find((value) => MessageWorkingFilterEnum[value.value] === workFilter)?.value,
		[workFilter],
	);

	const handleWorkFilterChange = useCallback(
		(item?: MessageWorkingFilterKeys) => {
			if (item && onChange) {
				onChange(
					{
						workFilter: MessageWorkingFilterEnum[item],
						dateFilter: dateFilter,
						timeFilter: timeFilter,
					},
					filterUuid,
				);
			}
		},
		[dateFilter, timeFilter, onChange, filterUuid],
	);

	const handleDateChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.target.value;
			const date = DateTime.fromISO(value).setZone(timezone);
			if (date.isValid && onChange && date > DateTime.now().setZone(timezone).minus({ days: 1 })) {
				onChange(
					{
						workFilter: workFilter,
						dateFilter: date,
						timeFilter: timeFilter,
					},
					filterUuid,
				);
			}
		},
		[workFilter, timeFilter, onChange, filterUuid, timezone],
	);

	const timeFrameClicked = useCallback((event: React.MouseEvent<HTMLElement>) => {
		setTimePickerAnchor(event.currentTarget);
	}, []);

	const onTimeChange = useCallback(
		({ newStartTime, newEndTime }: { newStartTime: DateTime; newEndTime: DateTime }) => {
			onChange?.(
				{
					workFilter: workFilter,
					dateFilter: dateFilter,
					timeFilter: {
						start: newStartTime,
						end: newEndTime,
					},
				},
				filterUuid,
			);
		},
		[workFilter, dateFilter, onChange, filterUuid],
	);

	const closeTimePicker = useCallback(() => {
		setTimePickerAnchor(null);
	}, []);

	const handleDeleteClicked = useCallback(() => {
		if (onDelete) {
			onDelete(filterUuid);
		}
	}, [onDelete, filterUuid]);

	return (
		<Box
			sx={{
				display: "flex",
				gap: 1,
				alignItems: "start",
				width: "100%",
				color: `${INK.SECONDARY} !important`,
				flexWrap: "wrap",
			}}
		>
			<SystemDropdown
				selectedItem={workFilterMemo}
				items={messageWorkingFilterItems}
				valueToString={(value) => (value ? MessageWorkingFilterEnum[value] : "")}
				onChange={handleWorkFilterChange}
				selectSx={{
					...commonStyle,
					width: "220px",
				}}
			/>
			<Box
				sx={{
					display: "flex",
					alignItems: "start",
					justifyContent: "start",
					gap: 1,
				}}
			>
				<SystemValidationTextField
					value={dateFilter.toFormat("yyyy-MM-dd")}
					variant={"outlined"}
					reserveHeightForHelperText={false}
					inputType={"date"}
					onChange={handleDateChange}
					inputProps={{
						inputProps: { min: DateTime.now().setZone(timezone).toFormat("yyyy-MM-dd") },
					}}
					inputSx={{
						...commonStyle,
					}}
					validation={{
						validationCallback: (value) =>
							DateTime.fromISO(value).setZone(timezone).isValid &&
							DateTime.fromISO(value).setZone(timezone) > DateTime.now().setZone(timezone).minus({ days: 1 }),
						showValidWhenValid: "never",
						performValidation: "onlyAfterInitialChange",
						helperText: {
							invalid: `Date must be after ${DateTime.now().setZone(timezone).toFormat("d MMM yyyy")}`,
						},
					}}
				/>
				{!isMobile && (
					<Box
						sx={{
							pt: 1,
						}}
					>
						({dateFilter.toFormat("ccc")})
					</Box>
				)}
			</Box>
			<Box
				sx={{
					display: "flex",
					height: "100%",
					alignItems: "start",
					gap: 1,
					justifyContent: "center",
				}}
			>
				{!isMobile && (
					<Box
						sx={{
							pt: 1,
						}}
					>
						at
					</Box>
				)}
				<button
					type={"button"}
					onClick={timeFrameClicked}
					style={{
						cursor: "pointer",
						border: `1px solid ${NEUTRAL[200]}`,
						borderRadius: "8px",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						height: "40px",
						paddingInline: 4,
						color: INK.SECONDARY,
						fontSize: "16px",
						...commonStyle,
					}}
				>
					{timeFilter.end.diff(timeFilter.start, "hours").as("hours") > 23.5
						? "All Day"
						: `${timeFilter.start.toFormat("h:mma")} - ${timeFilter.end.toFormat("h:mma")}`}
				</button>
			</Box>
			{onDelete && (
				<Box
					sx={{
						pt: 0.5,
					}}
				>
					<IconButton size={"small"} onClick={handleDeleteClicked}>
						<DeleteOutlineOutlinedIcon fontSize={"small"} />
					</IconButton>
				</Box>
			)}
			{timePickerAnchor && (
				<EditTimePopover
					startTime={timeFilter.start}
					endTime={timeFilter.end}
					onTimeChange={onTimeChange}
					onClose={closeTimePicker}
					anchorEl={timePickerAnchor}
					isActual={false}
				/>
			)}
		</Box>
	);
};
